// SequencePattern.js — patterned business document numbers (PO #, invoice #, …)
//
// Reuses the same token grammar as autokey patterns:
//   {{type}}     first 4 chars of type key (A–Z0–9, uppercase)
//   {{year}}     4-digit calendar year at assign time
//   {{seq}}      sequence number
//   {{seq:N}}    sequence zero-padded to N digits (1–12)
//
// Runtime pattern + next live on the base (`sequences`); schema may supply
// `defaultPattern` as a product fallback only.

import {
  formatAutokeyFromPattern,
  normalizeAutokeyPattern,
  parseAutokeyPattern,
  previewAutokeyPattern,
} from 'utilities/AutokeyPattern';

export const DEFAULT_SEQUENCE_PATTERN = '{{seq}}';
export const DEFAULT_PURCHASE_ORDER_SEQUENCE_PATTERN = 'PO-{{seq}}';

/**
 * @param {string} typeKey
 * @param {string} fieldName
 * @returns {string}
 */
export function buildSequenceSettingsKey(typeKey, fieldName) {
  const type = String(typeKey || '').trim().toUpperCase();
  const field = String(fieldName || '').trim();
  if (!type || !field) return '';
  return `${type}.${field}`;
}

/**
 * Counter doc id under `_bases/{baseKey}/counters/`.
 *
 * @param {string} typeKey
 * @param {string} fieldName
 * @returns {string}
 */
export function buildSequenceCounterDocId(typeKey, fieldName) {
  const type = String(typeKey || '').trim().toUpperCase();
  const field = String(fieldName || '').trim();
  if (!type || !field) return '';
  return `${type}__${field}`;
}

export function normalizeSequencePattern(raw) {
  return normalizeAutokeyPattern(raw);
}

export function parseSequencePattern(pattern) {
  return parseAutokeyPattern(pattern);
}

export function formatSequenceFromPattern(pattern, values = {}) {
  return formatAutokeyFromPattern(pattern, values);
}

export function previewSequencePattern(pattern, typeKey, options = {}) {
  const normalized = normalizeSequencePattern(pattern);
  if (!normalized) {
    return previewAutokeyPattern(DEFAULT_SEQUENCE_PATTERN, typeKey, options);
  }
  return previewAutokeyPattern(normalized, typeKey, options);
}

/**
 * Resolve the effective pattern for a Sequence field.
 *
 * Preference: base override → schema defaultPattern → {{seq}}.
 *
 * @param {{ pattern?: string }|null|undefined} baseEntry
 * @param {{ defaultPattern?: string }|null|undefined} schemaField
 * @returns {string}
 */
export function resolveSequencePattern(baseEntry, schemaField) {
  const fromBase = normalizeSequencePattern(baseEntry?.pattern);
  if (fromBase) return fromBase;
  const fromSchema = normalizeSequencePattern(schemaField?.defaultPattern);
  if (fromSchema) return fromSchema;
  return DEFAULT_SEQUENCE_PATTERN;
}
